import type { ComponentProps } from "react";
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";

const scrollVariants = tv({
  slots: {
    scrollBar:
      "flex touch-none p-px transition-colors select-none data-[state=hidden]:animate-out data-[state=hidden]:fade-out-20 data-[state=hidden]:zoom-out-95 data-[state=visible]:animate-in data-[state=visible]:fade-in-0 data-[state=visible]:zoom-in-95",
    thumb:
      "relative flex-1 rounded-full bg-neutral-500 transition-colors duration-150 active:bg-neutral-600",
    root: "relative size-full",
    viewport:
      "size-full rounded-[inherit] transition-[color,box-shadow] outline-none",
  },
  variants: {
    orientation: {
      vertical: {
        scrollBar: "h-full w-2.5 border-l border-l-transparent py-2.5",
      },
      horizontal: {
        scrollBar: "h-2.5 flex-col border-t border-t-transparent",
      },
    },
  },
});

const { scrollBar, thumb, root, viewport } = scrollVariants();

const ScrollBar = ({
  className,
  orientation = "vertical",
  ...props
}: ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) => {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={scrollBar({ className, orientation })}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className={thumb()}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
};

type ScrollAreaProps = ComponentProps<typeof ScrollAreaPrimitive.Root> &
  Pick<
    ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
    "orientation" | "id"
  >;

export const ScrollArea = ({
  className,
  children,
  orientation,
  id,
  ...props
}: ScrollAreaProps) => {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={root()}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        id={id}
        data-slot="scroll-area-viewport"
        className={viewport({ className })}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>

      <ScrollBar orientation={orientation} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
};
